import type { VaultMarket } from '@/features/evault-details/data/products'
import type { YieldSource } from '@/features/unified-earn/types'
import { TokenImageBySymbol } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { ArbitrageOpportunity, BorrowSource, DeltaStep } from '../types'
import { formatCompactUsd, formatRate, sourceClasses, sourceLabel } from './shared'

interface OpportunitiesListProps {
  opportunities: ArbitrageOpportunity[]
}

export function OpportunitiesList({ opportunities }: OpportunitiesListProps) {
  const shouldAnimate = useRef(true)
  useEffect(() => {
    shouldAnimate.current = false
  }, [])

  return (
    <div className="flex flex-col gap-1">
      <div className="hidden grid-cols-[1fr_auto_1fr_0.6fr_1.6fr] items-center gap-2 px-4 py-2 text-[11px] text-muted-foreground uppercase tracking-wider md:grid">
        <span>Borrow</span>
        <span />
        <span>Deposit</span>
        <span className="text-center">Spread</span>
        <span className="text-right">Rate Impact</span>
      </div>

      {opportunities.map((opp, i) => (
        <OpportunityListRow key={opp.id} opportunity={opp} index={i} animate={shouldAnimate.current} />
      ))}
    </div>
  )
}

function formatDelta(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '0.0'
  const sign = value > 0.005 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}`
}

function formatDeltaTriangle(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '0.0'
  const abs = Math.abs(value)
  if (abs < 0.05) return '0.0'
  const arrow = value > 0.05 ? '▲' : value < -0.05 ? '▼' : ''
  return `${arrow}${abs.toFixed(decimals)}`
}

function GradientSpread({ spread, className }: { spread: number; className?: string }) {
  return (
    <span
      className={cn(
        'bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-semibold text-transparent',
        className,
      )}
    >
      +{formatRate(spread)}%
    </span>
  )
}

function SourceBadge({
  source,
  market,
  className,
}: {
  source: BorrowSource | YieldSource
  market?: VaultMarket
  className?: string
}) {
  return (
    <span
      className={cn('rounded-full px-1.5 py-0.5 font-medium leading-none', sourceClasses(source, market), className)}
    >
      {sourceLabel(source, market)}
    </span>
  )
}

function IsolatedDepositWarningTooltip() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Isolated market warning"
          className="inline-flex items-center text-amber-500/80 transition-colors hover:text-amber-400"
        >
          <AlertTriangle className="h-3 w-3" />
        </button>
      </TooltipTrigger>
      <TooltipContentShort>
        <span className="block max-w-[280px] text-xs">
          Legacy Isolated markets are deprecated: deposits are safe and lending/withdrawals remain open, but new borrows
          and points are disabled. Consider moving assets to active markets.
        </span>
      </TooltipContentShort>
    </Tooltip>
  )
}

function spreadClass(value: number): string {
  if (value > 0.01) return 'text-gradient-green/70'
  if (value < -0.01) return 'text-red-400/70'
  return 'text-white/30'
}

function DeltaStepTable({ deltaSteps, variant }: { deltaSteps: DeltaStep[]; variant: 'desktop' | 'mobile' }) {
  const isDesktop = variant === 'desktop'
  const rateWarning = isDesktop ? 'text-gradient-yellow/60' : 'text-gradient-yellow/70'

  return (
    <div className={cn('rounded-md py-1.5', isDesktop ? 'bg-white/[0.03] px-2.5' : 'bg-white/[0.02] px-2')}>
      <div className={cn('flex flex-col', isDesktop && 'gap-0.5')}>
        <div
          className={cn(
            'grid grid-cols-4 text-[8px] text-white/25 uppercase tracking-wider',
            isDesktop ? 'gap-2' : 'mb-1 gap-1',
          )}
        >
          <span>Size</span>
          <span className="text-right">Borrow</span>
          <span className="text-right">Supply</span>
          <span className="text-right">Net</span>
        </div>
        {deltaSteps.map((step) => (
          <div
            key={step.pct}
            className={cn('grid grid-cols-4', isDesktop ? 'gap-2 text-[10px] leading-tight' : 'gap-1 text-[9px]')}
          >
            <span className="font-mono text-muted-foreground">{formatCompactUsd(step.tradeUsd)}</span>
            <span className={cn('text-right font-mono', step.borrowDelta > 0.05 ? rateWarning : 'text-white/30')}>
              {formatDeltaTriangle(step.borrowDelta)}%
            </span>
            <span className={cn('text-right font-mono', step.depositDelta < -0.05 ? rateWarning : 'text-white/30')}>
              {formatDeltaTriangle(step.depositDelta)}%
            </span>
            <span className={cn('text-right font-mono', isDesktop && 'font-medium', spreadClass(step.spread))}>
              {formatDelta(step.spread, 1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function OpportunityListRow({
  opportunity,
  index,
  animate,
}: { opportunity: ArbitrageOpportunity; index: number; animate: boolean }) {
  const { borrow, deposit, spread, deltaSteps } = opportunity

  return (
    <div
      className={cn(
        'group rounded-lg border border-transparent bg-white/[0.02] transition-all hover:border-white/10 hover:bg-white/[0.04]',
        animate && 'animate-sweep-card-in',
      )}
      style={animate ? { animationDelay: `${index * 0.03}s` } : undefined}
    >
      {/* Desktop row */}
      <div className="hidden grid-cols-[1fr_auto_1fr_0.6fr_1.6fr] items-center gap-2 px-4 py-3 md:grid">
        <div className="flex min-w-0 items-center gap-2.5">
          <TokenImageBySymbol symbol={borrow.asset} width={28} height={28} className="h-7 w-7 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-bold text-base text-foreground">{borrow.asset}</span>
              <span className="font-mono text-gradient-yellow text-sm">{formatRate(borrow.borrowAPY)}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <SourceBadge source={borrow.source} market={borrow.market} className="text-[10px]" />
              {borrow.pairName && (
                <span className="font-medium text-[11px] text-muted-foreground">{borrow.pairName}</span>
              )}
              {borrow.liquidity && (
                <span className="text-[11px] text-muted-foreground">
                  {formatCompactUsd(borrow.liquidity.availableUsd)} avail
                </span>
              )}
            </div>
          </div>
        </div>

        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-white/20" />

        <div className="flex min-w-0 items-center gap-2.5">
          <TokenImageBySymbol symbol={deposit.asset} width={28} height={28} className="h-7 w-7 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-bold text-base text-foreground">{deposit.asset}</span>
              <span className="font-mono text-gradient-green/80 text-sm">{formatRate(deposit.depositAPY)}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <SourceBadge source={deposit.source} market={deposit.market} className="text-[10px]" />
              {deposit.source === 'isolated' && <IsolatedDepositWarningTooltip />}
              {deposit.pairName && (
                <span className="font-medium text-[11px] text-muted-foreground">{deposit.pairName}</span>
              )}
              {deposit.liquidity && (
                <span className="text-[11px] text-muted-foreground">
                  {formatCompactUsd(deposit.liquidity.totalSupplyUsd)} TVL
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="text-center">
          <GradientSpread spread={spread} className="text-sm" />
        </div>

        {deltaSteps.length > 0 ? (
          <DeltaStepTable deltaSteps={deltaSteps} variant="desktop" />
        ) : (
          <div className="text-right text-[10px] text-muted-foreground">No data</div>
        )}
      </div>

      {/* Mobile row */}
      <div className="flex flex-col gap-2 p-3 md:hidden">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <TokenImageBySymbol
              symbol={borrow.asset}
              width={16}
              height={16}
              className="h-4 w-4 shrink-0 rounded-full"
            />
            <span className="font-bold text-foreground text-sm">{borrow.asset}</span>
            <span className="font-mono text-[11px] text-gradient-yellow">{formatRate(borrow.borrowAPY)}%</span>
            <ArrowRight className="h-2.5 w-2.5 shrink-0 text-white/20" />
            <TokenImageBySymbol
              symbol={deposit.asset}
              width={16}
              height={16}
              className="h-4 w-4 shrink-0 rounded-full"
            />
            <span className="font-bold text-foreground text-sm">{deposit.asset}</span>
            <span className="font-mono text-[11px] text-gradient-green/70">{formatRate(deposit.depositAPY)}%</span>
          </div>
          <GradientSpread spread={spread} className="shrink-0 font-bold text-sm" />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <SourceBadge source={borrow.source} market={borrow.market} className="text-[9px]" />
            {borrow.pairName && <span className="font-medium text-[9px] text-muted-foreground">{borrow.pairName}</span>}
            <ArrowRight className="h-2 w-2 text-white/15" />
            <SourceBadge source={deposit.source} market={deposit.market} className="text-[9px]" />
            {deposit.source === 'isolated' && <IsolatedDepositWarningTooltip />}
            {deposit.pairName && (
              <span className="font-medium text-[9px] text-muted-foreground">{deposit.pairName}</span>
            )}
          </div>
        </div>

        {deltaSteps.length > 0 && <DeltaStepTable deltaSteps={deltaSteps} variant="mobile" />}
      </div>
    </div>
  )
}
